import { IFlashcard } from '@sdk/contracts';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

import { useMutateFlashcardPracticeTranscribeAnswer } from './useMutateFlashcardPracticeTranscribeAnswer';

interface IStore {
  flashcardList: IFlashcard[];
  currentFlashcardId: string | null;
  showFlashcardAnswer: boolean;
  showWriteAnswer: boolean;
  setFlashcardList: (list: IFlashcard[]) => void;
  setShowFlashcardAnswer: (value: boolean) => void;
  setShowWriteAnswer: (value: boolean) => void;
  setCurrentFlashcardId: (id: string) => void;
}

const store = create<IStore>(set => ({
  flashcardList: [],
  currentFlashcardId: null,
  showFlashcardAnswer: false,
  showWriteAnswer: false,
  setFlashcardList: list => set({ flashcardList: list }),
  setCurrentFlashcardId: (id: string) => set({ currentFlashcardId: id }),
  setShowFlashcardAnswer: value => set({ showFlashcardAnswer: value }),
  setShowWriteAnswer: value => set({ showWriteAnswer: value })
}));

export const useFlashcardNavigator = (flashcardList: IFlashcard[]) => {
  const {
    currentFlashcardId,
    setCurrentFlashcardId,
    showFlashcardAnswer,
    setShowFlashcardAnswer,
    showWriteAnswer,
    setShowWriteAnswer
  } = store();
  const { setTranscription, transcription } = useMutateFlashcardPracticeTranscribeAnswer();

  useEffect(() => {
    if (flashcardList.length > 0 && !currentFlashcardId) {
      setCurrentFlashcardId(flashcardList[0]._id);
    }
  }, [flashcardList, currentFlashcardId]);

  const canUseKeyboardNavigation = useMemo(() => {
    return !(transcription.length > 0 && !showFlashcardAnswer) || showWriteAnswer;
  }, [transcription, showFlashcardAnswer, showWriteAnswer]);

  const currentFlashcard = useMemo(() => {
    return (
      flashcardList.find(flashcard => flashcard._id === currentFlashcardId) ?? flashcardList[0]
    );
  }, [flashcardList, currentFlashcardId]);

  const handleChangeFlashcard = () => {
    setShowFlashcardAnswer(false);
    setShowWriteAnswer(false);
    setTranscription('');
  };

  const canHideFlashcard = useMemo(() => {
    return flashcardList.length > 1;
  }, [flashcardList]);

  return {
    currentFlashcard,
    currentFlashcardId,
    handleChangeFlashcard,
    showFlashcardAnswer,
    setShowFlashcardAnswer,
    showWriteAnswer,
    setShowWriteAnswer,
    canHideFlashcard,
    setCurrentFlashcardId,
    canUseKeyboardNavigation
  };
};
